import { getConfig } from '../../utils/configUtils';

/**
 * Join an array of strings with Oxford commas.
 * @param {string[]} names - Array of strings to join
 * @returns {string}
 */
export function joinCommaSeparatedNames(names: string[]): string {
  const namesDeepCopy = [...names]; // Prevent unintended change of names array on re-render
  if (namesDeepCopy.length === 0) return '';
  if (namesDeepCopy.length === 1) return namesDeepCopy[0];
  if (namesDeepCopy.length === 2) return `${namesDeepCopy[0]} and ${namesDeepCopy[1]}`;
  const last = namesDeepCopy.pop();
  const joined = namesDeepCopy.join(', ');
  return `${joined}, and ${last}`;
}

/**
 * Compose arrival screen message for load.
 * @param {string[]} dropoffs - Array of human-readable destination location names
 * @param {boolean} isScheduled - Is the task scheduled
 * @param {boolean} isRestricted - Is the task restricted
 * @param {boolean} isCodeRequired - Does the task require a multi-factor code
 * @returns {string}
 */
export function composeArrivalLoadText(
  dropoffs: string[],
  isScheduled: boolean,
  isRestricted: boolean,
  isCodeRequired: boolean,
) {
  let prefix = 'Pickup';
  if (isScheduled) prefix = 'Scheduled pickup';
  if (isRestricted) prefix = 'Restricted pickup';
  if (isCodeRequired) prefix = 'Multi-factor pickup';
  return `${prefix} going to ` + joinCommaSeparatedNames(dropoffs);
}

/**
 * Compose arrival screen message for unload.
 * @param {string[]} pickups - Array of human-readable source location names
 * @param {boolean} isScheduled - Is the task scheduled
 * @param {boolean} isRestricted - Is the task restricted
 * @param {boolean} isCodeRequired - Does the task require a multi-factor code
 * @returns {string}
 */
export function composeArrivalUnloadText(
  pickups: string[],
  isScheduled: boolean,
  isRestricted: boolean,
  isCodeRequired: boolean,
) {
  let prefix = 'Drop-off';
  if (isScheduled) prefix = 'Scheduled drop-off';
  if (isRestricted) prefix = 'Restricted drop-off';
  if (isCodeRequired) prefix = 'Multi-factor drop-off';
  return `${prefix} from ` + joinCommaSeparatedNames(pickups);
}

export const GENERAL_PICKUP_TEXT = 'I am here for a pickup';
export const GENERAL_DROPOFF_TEXT = 'I am here for a drop-off';
const supportNumber = getConfig() && getConfig().site?.supportNumber ? getConfig().site?.supportNumber : '';
export const LOST_CONNECTION_TEXT = `I lost connection and can't show you more. Call Moxi support${
  supportNumber ? ` (${supportNumber})` : ''
} if you need detailed delivery info.`;

export function capitalizeStringArray(array: string[]) {
  return array.map((str) => str.charAt(0).toUpperCase() + str.slice(1));
}
